'use client';

import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  FullscreenTransformActionsContext,
  FullscreenTransformStateContext,
} from '@/app/contexts';
import { FULLSCREEN_ZOOM_SETTLE_MS } from '@/lib/fullscreen-zoom';

/** Latest debounced zoom step; readable synchronously from zoom button handlers. */
export const fullscreenSettledZoomRef = { current: 0 };

/**
 * Owns fullscreen pan/zoom/rotation state in a small subtree so zoom clicks do
 * not re-render ``Main`` (or the rest of the app). Preview zoom updates paint
 * immediately; settled zoom is debounced for crisp layout/raster passes.
 */
export function FullscreenTransformProvider({ children }: { children: ReactNode }) {
  const [zoomLevel, setZoomLevel] = useState(0);
  const [settledZoomLevel, setSettledZoomLevel] = useState(0);
  const [temporaryRotation, setTemporaryRotation] = useState<number | undefined>(undefined);
  const settledZoomLevelRef = useRef(0);

  // Sync before paint so zoom button handlers read the correct settled level on
  // the first click after mount (module ref survives provider unmount).
  useLayoutEffect(() => {
    settledZoomLevelRef.current = settledZoomLevel;
    fullscreenSettledZoomRef.current = settledZoomLevel;
  }, [settledZoomLevel]);

  useEffect(() => {
    return () => {
      fullscreenSettledZoomRef.current = 0;
    };
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSettledZoomLevel(zoomLevel);
      settledZoomLevelRef.current = zoomLevel;
      fullscreenSettledZoomRef.current = zoomLevel;
    }, FULLSCREEN_ZOOM_SETTLE_MS);
    return () => window.clearTimeout(timer);
  }, [zoomLevel]);

  const stateValue = useMemo(
    () => ({
      zoomLevel,
      settledZoomLevel,
      temporaryRotation,
    }),
    [zoomLevel, settledZoomLevel, temporaryRotation],
  );

  const actionsValue = useMemo(
    () => ({
      setZoomLevel,
      setTemporaryRotation,
    }),
    [],
  );

  return (
    <FullscreenTransformActionsContext.Provider value={actionsValue}>
      <FullscreenTransformStateContext.Provider value={stateValue}>
        {children}
      </FullscreenTransformStateContext.Provider>
    </FullscreenTransformActionsContext.Provider>
  );
}
